import { useState, useEffect, useRef } from 'react';
import {
  Shield, HelpCircle, User, LogOut, Hash, FileText, Settings,
  ChevronDown, ChevronUp, X, MessageSquare, LifeBuoy,Book, ArrowRight, Lock, Cpu, Search, ShieldCheck, Download, Briefcase,
  Globe, CreditCard, Bell
} from 'lucide-react';
import axios from 'axios';
import Avatar from '../../../services/Avatar';
import { Link } from 'react-router-dom';


const HashVaultNavigation = ({ activeTab, setActiveTab, algorithms  }) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(true);
  const profileRef = useRef(null);
  const helpRef = useRef(null);
  const helpModalRef = useRef(null);

  const Skeleton = ({ width = "w-8", height = "h-8", rounded = "rounded-full" }) => (
    <div className={`${width} ${height} ${rounded} bg-gray-300 animate-pulse`} />
  );

    // Helper functions for algorithm display
const getAlgorithmSpeed = (id) => {
  // Return percentage based on relative speed
  const speeds = {
    'md5': 100, 'sha1': 95, 'sha224': 85, 
    'sha256': 80, 'sha384': 70, 'sha512': 65,
    'blake2b': 90, 'blake2s': 92, 'sha3-224': 75,
    'sha3-256': 70, 'sha3-384': 60, 'sha3-512': 55,
    'argon2': 30, 'bcrypt': 40, 'pbkdf2_sha256': 50,
    'pbkdf2_sha512': 45
  };
  return speeds[id] || 50;
};

const getAlgorithmUseCase = (id) => {
  const useCases = {
    'md5': 'Legacy checksums',
    'sha1': 'Basic integrity',
    'sha256': 'General purpose',
    'sha512': 'High-security',
    'blake2b': 'Fast hashing',
    'sha3': 'Post-quantum',
    'argon2': 'Password hashing',
    'bcrypt': 'Password storage',
    'pbkdf2': 'Key derivation'
  };
  
  for (const [key, value] of Object.entries(useCases)) {
    if (id.includes(key.toLowerCase())) return value;
  }
  return 'Various uses';
};

  useEffect(() => {
          const controller = new AbortController();
          const fetchUserProfile = async () => {
              try {
                  const response = await axios.get('/users/me', { 
                      withCredentials: true,
                      signal: controller.signal // Attach signal to cancel duplicate requests
                  });
                  setCurrentUser(response.data);
              } catch (error) {
                  if (axios.isCancel(error)) {
                      console.log('Request canceled:', error.message);
                  } else {
                      console.error('Error fetching user profile:', error);
                  }
              }
          };
      
          fetchUserProfile();
      
          return () => controller.abort(); // Cleanup function to cancel request on re-render
      }, []);

   // Close dropdowns when clicking outside
   useEffect(() => {
    const handleClickOutside = (event) => {
      // For profile dropdown
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
  
      // For help dropdown, ensure it only closes if clicking outside both help button and modal
      if (
        helpRef.current &&
        !helpRef.current.contains(event.target) &&
        helpModalRef.current &&
        !helpModalRef.current.contains(event.target)
      ) {
        setIsHelpOpen(false);
      }
    };
  
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isHelpOpen]);
  

  const handleLogout = async () => {
    try {
      const response = await fetch('/d-auth/logout', {
        method: 'POST', // or 'GET', depending on your backend implementation
        credentials: 'include', // include cookies with the request
        headers: {
          'Content-Type': 'application/json',
        },
      });
  
      if (response.ok) {       
        
        window.location.href = '/'; 
      } else {
        // If there's an error during logout
        const errorData = await response.json();
        //console.error('Logout failed', errorData);
        alert( errorData ||'Logout failed. Please try again.');
      }
    } catch (error) {
      //console.error('Error during logout:', error);
      alert('An error occurred. Please try again.');
    }
  };

  return (
    <>
      {/* Main Navigation Bar */}
      <nav className="bg-gray-900/80 backdrop-blur-xl border-b border-gray-800 sticky top-0 z-50 shadow-2xl">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo/Brand */}
            <Link to="/" className="flex items-center space-x-3">
              <div className="p-1.5 rounded-lg bg-gradient-to-br from-blue-600 to-blue-800 shadow-lg animate-pulse-slow">
                  <Shield className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-500 tracking-tight">
                  HashVault Pro
              </span>
          </Link>

            {/* Tab Navigation */}
            <div className="hidden md:flex items-center space-x-1 bg-gray-800/50 rounded-lg p-1 border border-gray-700/50">
              <button
                onClick={() => setActiveTab('hashboard')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 ${
                  activeTab === 'hashboard'
                    ? 'bg-blue-600/90 text-white shadow-lg'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Hash className="w-4 h-4" />
                  <span>Hashboard</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('integrity')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 ${
                  activeTab === 'integrity'
                    ? 'bg-blue-600/90 text-white shadow-lg'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <FileText className="w-4 h-4" />
                  <span>IntegrityBoard</span>
                </div>
              </button>
            </div>

            {/* Right Side Controls */}
            <div className="flex items-center space-x-4">
              {/* Notification Bell */}
              <button 
                className="p-2 relative rounded-full hover:bg-gray-700/50 transition-all duration-200 group"
                onClick={() => setHasUnreadNotifications(false)}
              >
                <Bell className="w-5 h-5 text-gray-400 group-hover:text-white" />
                {hasUnreadNotifications && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full animate-ping"></span>
                )}
              </button>

              {/* Help Center */}
              <div className="relative" ref={helpRef}>
                <button
                  onClick={() => setIsHelpOpen(!isHelpOpen)}
                  className="flex items-center space-x-1 px-3 py-1.5 rounded-md hover:bg-gray-700/50 transition-all duration-200 group"
                >
                  <HelpCircle className="w-5 h-5 text-gray-400 group-hover:text-blue-400" />
                  <span className="text-sm hidden md:inline-block">Help</span>
                  {isHelpOpen ? (
                    <ChevronUp className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  )}
                </button>

                {/* Help Center Dropdown */}
                {isHelpOpen && (
                  <div className="absolute right-0 mt-2 w-72 bg-gray-800 rounded-xl shadow-2xl border border-gray-700/50 overflow-hidden animate-fade-in">
                    <div className="p-4 border-b border-gray-700/50 bg-gray-800/50">
                      <h3 className="text-lg font-semibold text-white flex items-center">
                        <LifeBuoy className="w-5 h-5 mr-2 text-blue-400" />
                        Help Center
                      </h3>
                    </div>
                    <div className="divide-y divide-gray-700/50">
                      <a href="#" className="flex items-center px-4 py-3 hover:bg-gray-700/30 transition-colors">
                        <MessageSquare className="w-5 h-5 mr-3 text-blue-400" />
                        <div>
                          <p className="text-sm font-medium text-white">Contact Support</p>
                          <p className="text-xs text-gray-400">24/7 live assistance</p>
                        </div>
                      </a>
                      <a href="#" className="flex items-center px-4 py-3 hover:bg-gray-700/30 transition-colors">
                        <Globe className="w-5 h-5 mr-3 text-blue-400" />
                        <div>
                          <p className="text-sm font-medium text-white">Documentation</p>
                          <p className="text-xs text-gray-400">API references & guides</p>
                        </div>
                      </a>
                      <a href="#" className="flex items-center px-4 py-3 hover:bg-gray-700/30 transition-colors">
                        <CreditCard className="w-5 h-5 mr-3 text-blue-400" />
                        <div>
                          <p className="text-sm font-medium text-white">Billing Help</p>
                          <p className="text-xs text-gray-400">Subscription questions</p>
                        </div>
                      </a>
                    </div>
                    <div className="p-3 bg-gray-900/30 text-center">
                      <button className="text-xs text-blue-400 hover:underline">
                        View all help resources
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* User Profile */}
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className="flex items-center space-x-2 px-3 py-1 rounded-md hover:bg-gray-700/50 transition-all duration-200 group"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-white font-medium">
                  {currentUser ? (
                      <>
                        <Avatar firstName={currentUser.first_name} lastName={currentUser.last_name} />
                      </>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Skeleton width="w-10 h-10" rounded="rounded-full" /> {/* Skeleton for Avatar */}
                        <div className="flex flex-col">
                          <Skeleton width="w-20" height="h-3" rounded="rounded-md" /> {/* Skeleton for Name */}
                          <Skeleton width="w-16" height="h-3" rounded="rounded-md" /> {/* Skeleton for Role */}
                        </div>
                      </div>
                    )}
                  </div>
                  <span className="text-sm hidden md:inline-block">
                  {currentUser ? (
                                                <>
                                                 
                                                  <div>
                                                    <span className="text-sm text-gray-100 font-medium">{currentUser.first_name} {currentUser.last_name}</span>
                                               
                                                  </div>
                                                </>
                                              ) : (
                                                <div className="flex items-center gap-2">
                                                  <Skeleton width="w-10 h-10" rounded="rounded-full" /> {/* Skeleton for Avatar */}
                                                  <div className="flex flex-col">
                                                    <Skeleton width="w-20" height="h-3" rounded="rounded-md" /> {/* Skeleton for Name */}
                                                    <Skeleton width="w-16" height="h-3" rounded="rounded-md" /> {/* Skeleton for Role */}
                                                  </div>
                                                </div>
                                              )}
                  </span>
                  {isProfileOpen ? (
                    <ChevronUp className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  )}
                </button>

                {/* Profile Dropdown */}
                {isProfileOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-gray-800 rounded-xl shadow-2xl border border-gray-700/50 overflow-hidden animate-fade-in">
                    <div className="p-4 border-b border-gray-700/50 bg-gray-800/50">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-white font-medium">
                        {currentUser ? (
                      <>
                        <Avatar firstName={currentUser.first_name} lastName={currentUser.last_name} />
                      </>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Skeleton width="w-10 h-10" rounded="rounded-full" /> {/* Skeleton for Avatar */}
                        <div className="flex flex-col">
                          <Skeleton width="w-20" height="h-3" rounded="rounded-md" /> {/* Skeleton for Name */}
                          <Skeleton width="w-16" height="h-3" rounded="rounded-md" /> {/* Skeleton for Role */}
                        </div>
                      </div>
                    )}
                        </div>
                        <div>
                            {currentUser ? (
                                                <>
                                                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-white text-sm font-medium">
                                                    <Avatar firstName={currentUser.first_name} lastName={currentUser.last_name} />
                                                  </div>
                                                  <div>
                                                    <span className="text-sm text-gray-100 font-medium">{currentUser.first_name} {currentUser.last_name}</span>
                                                    <span className="text-xs text-gray-400">{currentUser.role}</span>
                                                  </div>
                                                </>
                                              ) : (
                                                <div className="flex items-center gap-2">
                                                  <Skeleton width="w-10 h-10" rounded="rounded-full" /> {/* Skeleton for Avatar */}
                                                  <div className="flex flex-col">
                                                    <Skeleton width="w-20" height="h-3" rounded="rounded-md" /> {/* Skeleton for Name */}
                                                    <Skeleton width="w-16" height="h-3" rounded="rounded-md" /> {/* Skeleton for Role */}
                                                  </div>
                                                </div>
                                              )}
                        </div>
                      </div>
                    </div>
                    <div className="divide-y divide-gray-700/50">
                      <a href="#" className="flex items-center px-4 py-3 hover:bg-gray-700/30 transition-colors">
                        <User className="w-5 h-5 mr-3 text-gray-400" />
                        <span className="text-sm">Profile Settings</span>
                      </a>
                      <a href="#" className="flex items-center px-4 py-3 hover:bg-gray-700/30 transition-colors">
                        <CreditCard className="w-5 h-5 mr-3 text-gray-400" />
                        <span className="text-sm">Billing</span>
                      </a>
                      <a href="#" className="flex items-center px-4 py-3 hover:bg-gray-700/30 transition-colors">
                        <Settings className="w-5 h-5 mr-3 text-gray-400" />
                        <span className="text-sm">Preferences</span>
                      </a>
                    </div>
                    <div className="p-3 border-t border-gray-700/50">
                      <button className="flex items-center justify-center w-full px-3 py-2 rounded-lg bg-red-600/20 hover:bg-red-600/30 text-red-400 transition-colors"
                       onClick={handleLogout} 
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        <span className="text-sm">Sign Out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile Tab Navigation */}
          <div className="md:hidden flex justify-center space-x-1 pb-2 px-4">
            <button
              onClick={() => setActiveTab('hashboard')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                activeTab === 'hashboard'
                  ? 'bg-blue-600/90 text-white shadow-md'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
              }`}
            >
              Hashboard
            </button>
            <button
              onClick={() => setActiveTab('integrity')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                activeTab === 'integrity'
                  ? 'bg-blue-600/90 text-white shadow-md'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
              }`}
            >
              IntegrityBoard
            </button>
          </div>
        </div>
      </nav>

       

      {/* Help Center Modal */} 
      {isHelpOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in"
          onClick={() => setIsHelpOpen(false)}
        >
          <div 
            ref={helpModalRef}
            className="relative w-full max-w-4xl bg-gray-800 rounded-xl border border-gray-700/50 shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Animated gradient header bar */}
            <div className="h-1 bg-gradient-to-r from-blue-500 via-purple-600 to-pink-500 animate-gradient-x"></div>
            
            <div className="p-6 overflow-y-auto flex-1">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-white flex items-center">
                    <LifeBuoy className="w-6 h-6 mr-3 text-blue-400" />
                    <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                      Security Help Center
                    </span>
                  </h2>
                  <p className="text-gray-400 mt-1 flex items-center">
                    <Shield className="w-4 h-4 mr-2 text-green-400" />
                    All communications are end-to-end encrypted
                  </p>
                </div>
                <button
                  onClick={() => setIsHelpOpen(false)}
                  className="p-2 rounded-full hover:bg-gray-700/50 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              {/* Three-column feature grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                {/* Contact Card */}
                <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 hover:from-gray-700/50 hover:to-gray-800/50 rounded-xl p-5 border border-gray-700/50 transition-all cursor-pointer group hover:shadow-lg hover:border-blue-500/30">
                  <div className="flex items-center mb-4">
                    <div className="p-2 mr-3 bg-blue-500/10 rounded-lg group-hover:bg-blue-500/20 transition-colors">
                      <MessageSquare className="w-5 h-5 text-blue-400 group-hover:text-blue-300" />
                    </div>
                    <h3 className="font-medium text-white group-hover:text-blue-300">Live Support</h3>
                  </div>
                  <p className="text-sm text-gray-400 mb-4">24/7 encrypted chat with our security experts</p>
                  <div className="flex items-center text-xs text-blue-400">
                    <span>Instant connection</span>
                    <div className="ml-2 w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                  </div>
                </div>

                {/* Knowledge Base Card */}
                <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 hover:from-gray-700/50 hover:to-gray-800/50 rounded-xl p-5 border border-gray-700/50 transition-all cursor-pointer group hover:shadow-lg hover:border-purple-500/30">
                  <div className="flex items-center mb-4">
                    <div className="p-2 mr-3 bg-purple-500/10 rounded-lg group-hover:bg-purple-500/20 transition-colors">
                      <Book className="w-5 h-5 text-purple-400 group-hover:text-purple-300" />
                    </div>
                    <h3 className="font-medium text-white group-hover:text-purple-300">Knowledge Base</h3>
                  </div>
                  <p className="text-sm text-gray-400 mb-4">Comprehensive security documentation & guides</p>
                  <div className="flex items-center text-xs text-purple-400">
                    <span>300+ articles</span>
                    <ArrowRight className="w-3 h-3 ml-2" />
                  </div>
                </div>

                {/* Algorithm Guide Card */}
                <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 hover:from-gray-700/50 hover:to-gray-800/50 rounded-xl p-5 border border-gray-700/50 transition-all cursor-pointer group hover:shadow-lg hover:border-green-500/30">
                  <div className="flex items-center mb-4">
                    <div className="p-2 mr-3 bg-green-500/10 rounded-lg group-hover:bg-green-500/20 transition-colors">
                      <Lock className="w-5 h-5 text-green-400 group-hover:text-green-300" />
                    </div>
                    <h3 className="font-medium text-white group-hover:text-green-300">Algorithm Guide</h3>
                  </div>
                  <p className="text-sm text-gray-400 mb-4">Compare cryptographic hash algorithms</p>
                  <div className="flex items-center text-xs text-green-400">
                    <span>16 algorithms</span>
                    <ChevronDown className="w-3 h-3 ml-2" />
                  </div>
                </div>
              </div>

              {/* Algorithm Comparison Section */}
              <div className="mb-8">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                  <Cpu className="w-5 h-5 mr-2 text-blue-400" />
                  Cryptographic Algorithms
                </h3>
                <div className="bg-gray-900/30 rounded-lg overflow-hidden border border-gray-700/50">
                  <div className="grid grid-cols-12 bg-gray-800/50 p-3 text-sm font-medium text-gray-400 border-b border-gray-700/50">
                    <div className="col-span-4">Algorithm</div>
                    <div className="col-span-2">Security</div>
                    <div className="col-span-3">Speed</div>
                    <div className="col-span-3">Use Case</div>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {algorithms.map((algo) => (
                      <div 
                        key={algo.id} 
                        className="grid grid-cols-12 p-3 text-sm border-b border-gray-700/50 hover:bg-gray-700/20 transition-colors"
                      >
                        <div className="col-span-4 font-medium text-white">{algo.name}</div>
                        <div className="col-span-2">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            algo.security === 'Very High' ? 'bg-green-900/50 text-green-400' :
                            algo.security === 'High' ? 'bg-blue-900/50 text-blue-400' :
                            algo.security === 'Medium' ? 'bg-yellow-900/50 text-yellow-400' :
                            'bg-red-900/50 text-red-400'
                          }`}>
                            {algo.security}
                          </span>
                        </div>
                        <div className="col-span-3">
                          <div className="w-full bg-gray-700 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                algo.id.includes('sha3') || algo.id.includes('argon2') ? 'bg-purple-500' :
                                algo.id.includes('sha512') || algo.id.includes('blake2') ? 'bg-blue-500' :
                                algo.id.includes('sha256') ? 'bg-green-500' :
                                'bg-yellow-500'
                              }`}
                              style={{ width: `${getAlgorithmSpeed(algo.id)}%` }}
                            ></div>
                          </div>
                        </div>
                        <div className="col-span-3 text-gray-400">
                          {getAlgorithmUseCase(algo.id)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Quick Search */}
              <div className="bg-gray-900/30 rounded-lg p-5 border border-gray-700/50 mb-6">
                <h3 className="font-medium text-white mb-3 flex items-center">
                  <Search className="w-5 h-5 mr-2 text-blue-400" />
                  Quick Search
                </h3>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search security topics, algorithms, or issues..."
                    className="w-full bg-gray-800 border border-gray-700/50 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent pl-10"
                  />
                  <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                </div>
              </div>
            </div>

            {/* Footer with animated gradient */}
            <div className="bg-gradient-to-r from-gray-900/30 via-gray-800/30 to-gray-900/30 p-4 border-t border-gray-700/50 flex justify-between items-center">
              <div className="flex items-center">
                <ShieldCheck className="w-5 h-5 mr-2 text-green-400" />
                <p className="text-sm text-gray-400">Enterprise-grade security</p>
              </div>
              <div className="flex space-x-3">
                <button className="px-4 py-2 bg-gray-700/50 hover:bg-gray-600/50 rounded-lg text-sm font-medium transition-colors flex items-center">
                  <Download className="w-4 h-4 mr-2" />
                  Docs
                </button>
                <button className="px-4 py-2 bg-blue-600/90 hover:bg-blue-500 rounded-lg text-sm font-medium transition-colors flex items-center">
                  <Briefcase className="w-4 h-4 mr-2" />
                  Enterprise
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HashVaultNavigation;